// src/App.tsx
import React, { useState } from "react";
import {
  Layout,
  Typography,
  Tabs,
  Button,
  Card,
  Space,
  Tag,
  Result,
  Spin,
  message,
  Progress,
  ConfigProvider,
  Row,
} from "antd";
import {
  CarOutlined,
  SafetyCertificateFilled,
  CloseCircleFilled,
  RocketOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import { fetchExamQuestions } from "./services/api";
import { LicenseType, QuizState } from "./types";
import "./App.css";

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<LicenseType>("A1");
  const [quizState, setQuizState] = useState<QuizState>({
    questions: [],
    currentQuestionIndex: 0,
    userAnswers: {},
    score: 0,
    isFinished: false,
    loading: false,
  });

  // --- Logic Functions (Kept mostly the same, just refined) ---
  const startExam = async () => {
    setQuizState((prev) => ({
      ...prev,
      loading: true,
      isFinished: false,
      userAnswers: {},
      score: 0,
      currentQuestionIndex: 0,
    }));
    try {
      const questions = await fetchExamQuestions(activeTab);
      setQuizState((prev) => ({
        ...prev,
        questions,
        loading: false,
      }));
    } catch (error) {
      message.error("Không thể kết nối với AI. Vui lòng thử lại sau!");
      setQuizState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleAnswer = (idx: number) => {
    const currentQ = quizState.questions[quizState.currentQuestionIndex];
    setQuizState((prev) => ({
      ...prev,
      userAnswers: {
        ...prev.userAnswers,
        [currentQ.id]: idx,
      },
    }));
  };

  const submitExam = () => {
    let score = 0;
    quizState.questions.forEach((q) => {
      if (quizState.userAnswers[q.id] === q.correctAnswer) {
        score++;
      }
    });
    setQuizState((prev) => ({ ...prev, isFinished: true, score }));
  };

  // --- Render Components ---

  const renderQuiz = () => {
    if (quizState.loading)
      return (
        <div style={{ textAlign: "center", padding: "80px 20px" }}>
          <Spin size="large" />
          <Title level={4} style={{ marginTop: 20, color: "#8c8c8c" }}>
            Đang chuẩn bị đề thi cho bạn...
          </Title>
          <Text type="secondary">
            Hệ thống đang tổng hợp câu hỏi mới nhất từ bộ đề.
          </Text>
        </div>
      );

    if (quizState.questions.length === 0)
      return (
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <RocketOutlined
            style={{ fontSize: 64, color: "#fa8c16", marginBottom: 20 }}
          />
          <Title level={2}>Sẵn sàng thi thử?</Title>
          <Paragraph
            type="secondary"
            style={{ fontSize: 16, maxWidth: 500, margin: "0 auto 30px" }}
          >
            Hệ thống sẽ tạo ngẫu nhiên đề thi mô phỏng theo cấu trúc chuẩn của
            hạng bằng
            <strong style={{ color: "#fa8c16" }}> {activeTab}</strong>. Chúc bạn
            đạt kết quả tốt!
          </Paragraph>
          <Button
            type="primary"
            size="large"
            onClick={startExam}
            shape="round"
            style={{ height: 50, padding: "0 40px", fontSize: 18 }}
            icon={<ReadOutlined />}
          >
            Bắt đầu làm bài
          </Button>
        </div>
      );

    if (quizState.isFinished) return renderResult();

    const currentQ = quizState.questions[quizState.currentQuestionIndex];
    const userSelected = quizState.userAnswers[currentQ.id];

    return (
      <div style={{ animation: "fadeIn 0.5s" }}>
        {/* Progress Bar */}
        <div style={{ marginBottom: 24 }}>
          <Row justify="space-between" align="middle">
            <Text strong type="secondary">
              Tiến độ
            </Text>
            <Text strong>
              {quizState.currentQuestionIndex + 1}/{quizState.questions.length}
            </Text>
          </Row>
          <Progress
            percent={Math.round(
              ((quizState.currentQuestionIndex + 1) /
                quizState.questions.length) *
                100,
            )}
            strokeColor="#fa8c16"
            showInfo={false}
            size="small"
          />
        </div>

        {/* Question Card */}
        <Card
          bordered={false}
          style={{ boxShadow: "none", background: "transparent" }}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ marginBottom: 16 }}>
            {currentQ.isParalysis && (
              <Tag
                color="error"
                style={{
                  padding: "4px 10px",
                  borderRadius: 20,
                  marginBottom: 8,
                }}
              >
                ⚠️ Câu điểm liệt
              </Tag>
            )}
            <Title level={4} style={{ marginTop: 0 }}>
              {currentQ.question}
            </Title>
          </div>

          <Space orientation="vertical" style={{ width: "100%" }} size={0}>
            {currentQ.options.map((opt, idx) => (
              <div
                key={idx}
                className={`answer-option-card ${userSelected === idx ? "selected" : ""}`}
                onClick={() => handleAnswer(idx)}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    border:
                      userSelected === idx
                        ? "6px solid #fa8c16"
                        : "2px solid #d9d9d9",
                    marginRight: 16,
                    flexShrink: 0,
                  }}
                />
                <Text style={{ fontSize: 16 }}>{opt}</Text>
              </div>
            ))}
          </Space>

          <div
            style={{
              marginTop: 32,
              display: "flex",
              justifyContent: "space-between",
              borderTop: "1px solid #f0f0f0",
              paddingTop: 20,
            }}
          >
            <Button
              size="large"
              disabled={quizState.currentQuestionIndex === 0}
              onClick={() =>
                setQuizState((prev) => ({
                  ...prev,
                  currentQuestionIndex: prev.currentQuestionIndex - 1,
                }))
              }
              type="text"
            >
              Quay lại
            </Button>

            {quizState.currentQuestionIndex < quizState.questions.length - 1 ? (
              <Button
                type="primary"
                size="large"
                shape="round"
                onClick={() =>
                  setQuizState((prev) => ({
                    ...prev,
                    currentQuestionIndex: prev.currentQuestionIndex + 1,
                  }))
                }
              >
                Câu tiếp theo
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                shape="round"
                danger
                onClick={submitExam}
              >
                Nộp bài thi
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  };

  const renderResult = () => {
    const total = quizState.questions.length;
    const percent = Math.round((quizState.score / total) * 100);
    const isPassed = percent >= 80; // Example logic

    return (
      <div style={{ animation: "slideUp 0.5s" }}>
        <Result
          icon={
            isPassed ? (
              <SafetyCertificateFilled style={{ color: "#52c41a" }} />
            ) : (
              <CloseCircleFilled style={{ color: "#ff4d4f" }} />
            )
          }
          status={isPassed ? "success" : "error"}
          title={
            isPassed
              ? "Chúc mừng! Bạn đã đạt yêu cầu"
              : "Rất tiếc, bạn chưa đạt yêu cầu"
          }
          subTitle={
            <Space orientation="vertical">
              <Text>
                Bạn trả lời đúng{" "}
                <strong style={{ fontSize: 18 }}>
                  {quizState.score}/{total}
                </strong>{" "}
                câu hỏi.
              </Text>
              <div style={{ width: 200, margin: "10px auto" }}>
                <Progress
                  type="circle"
                  percent={percent}
                  strokeColor={isPassed ? "#52c41a" : "#ff4d4f"}
                  size={80}
                />
              </div>
            </Space>
          }
          extra={[
            <Button
              type="primary"
              size="large"
              shape="round"
              key="retry"
              onClick={startExam}
            >
              Thi lại đề mới
            </Button>,
          ]}
        />

        <div style={{ marginTop: 40 }}>
          <Title level={4} style={{ textAlign: "center", marginBottom: 24 }}>
            Chi tiết bài làm
          </Title>
          <Space orientation="vertical" style={{ width: "100%" }} size="large">
            {quizState.questions.map((q, idx) => {
              const userAns = quizState.userAnswers[q.id];
              const isCorrect = userAns === q.correctAnswer;
              return (
                <Card
                  bordered={false}
                  key={idx}
                  hoverable
                  style={{
                    borderRadius: 12,
                    borderLeft: `6px solid ${isCorrect ? "#b7eb8f" : "#ffa39e"}`,
                    overflow: "hidden",
                  }}
                >
                  <Title level={5} style={{ marginTop: 0 }}>
                    <Text type="secondary" style={{ marginRight: 8 }}>
                      Câu {idx + 1}:
                    </Text>
                    {q.question}
                  </Title>

                  <div
                    style={{
                      background: "#fafafa",
                      padding: 15,
                      borderRadius: 8,
                      marginTop: 10,
                    }}
                  >
                    <Space orientation="vertical" style={{ width: "100%" }}>
                      <Text>
                        Lựa chọn của bạn:{" "}
                        {userAns !== undefined ? (
                          <Tag color={isCorrect ? "success" : "error"}>
                            {q.options[userAns]}
                          </Tag>
                        ) : (
                          <Tag>Bỏ qua</Tag>
                        )}
                      </Text>

                      {!isCorrect && (
                        <Text>
                          Đáp án đúng:{" "}
                          <Tag color="success">
                            {q.options[q.correctAnswer]}
                          </Tag>
                        </Text>
                      )}

                      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                        <ReadOutlined
                          style={{ color: "#fa8c16", marginTop: 4 }}
                        />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {q.explanation}
                        </Text>
                      </div>
                    </Space>
                  </div>
                </Card>
              );
            })}
          </Space>
        </div>
      </div>
    );
  };

  const tabItems = [
    { label: "Xe máy A1", key: "A1" },
    { label: "Mô tô A2", key: "A2" },
    { label: "Ô tô B1", key: "B1" },
    { label: "Ô tô B2", key: "B2" },
    { label: "Xe tải C", key: "C" },
  ];

  // --- Theme Configuration ---
  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Montserrat', sans-serif",
          colorPrimary: "#fa8c16", // Warm Orange
          borderRadius: 12,
          colorBgContainer: "#ffffff",
          colorTextHeading: "#434343",
        },
        components: {
          Button: {
            fontWeight: 600,
            controlHeightLG: 48,
          },
          Tabs: {
            itemSelectedColor: "#fa8c16",
            itemHoverColor: "#ffd591",
            inkBarColor: "#fa8c16",
            titleFontSize: 16,
          },
          Layout: {
            bodyBg: "transparent",
            headerBg: "#ffffff",
            footerBg: "transparent",
          },
        },
      }}
    >
      <Layout style={{ minHeight: "100vh" }}>
        <Header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            zIndex: 10,
            padding: "0 24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                background: "linear-gradient(45deg, #fa8c16, #ffa940)",
                padding: 8,
                borderRadius: 8,
                marginRight: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CarOutlined style={{ color: "#fff", fontSize: "20px" }} />
            </div>
            <Title
              level={4}
              style={{ margin: 0, color: "#fa8c16", letterSpacing: -0.5 }}
            >
              Luyện Thi Lái Xe Với AI
            </Title>
          </div>
        </Header>

        <Content
          style={{
            padding: "30px 20px",
            maxWidth: 900,
            margin: "0 auto",
            width: "100%",
          }}
        >
          <Card
            bordered={false}
            style={{
              boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
              borderRadius: 24,
              overflow: "hidden",
            }}
            styles={{ body: { padding: 0 } }}
          >
            <Tabs
              defaultActiveKey="A1"
              centered
              items={tabItems.map((item) => ({ ...item, children: null }))}
              onChange={(key) => {
                setActiveTab(key as LicenseType);
                setQuizState((prev) => ({
                  ...prev,
                  questions: [],
                  isFinished: false,
                }));
              }}
              tabBarStyle={{
                margin: 0,
                padding: "16px 0",
                borderBottom: "1px solid #f0f0f0",
              }}
            />

            <div style={{ padding: "32px 24px", minHeight: 400 }}>
              {renderQuiz()}
            </div>
          </Card>
        </Content>

        <Footer style={{ textAlign: "center", color: "#8c8c8c" }}>
          <Space
            separator={
              <div style={{ width: 1, height: 10, background: "#ccc" }} />
            }
          >
            <Text type="secondary">
              Luyện thi Lái xe AI © {new Date().getFullYear()}
            </Text>
          </Space>
        </Footer>
      </Layout>
    </ConfigProvider>
  );
};

export default App;
